import BleachAPI from "@/integration/BleachAPI"

import { getStoreConfig, parseStoreConfigFromUrl } from "@/utils"
import { getRouter } from "@/router"

import { regionOptions } from "./constants"

export default {
  namespaced: true,

  state: {
    localeOptions: null,
    localeConfig: {
      countryCode: "",
      language: null,
      market: null
    },
    suggestedLocaleConfig: null,
    showLocaleSuggestionModal: false,
    queueGeoFetch: false
  },

  mutations: {
    SET_LOCALE_OPTIONS: (state, payload) => {
      state.localeOptions = [...payload]
    },

    SET_LOCALE_VALUES: (
      state,
      payload /*{ countryCode, marketName, language }*/
    ) => {
      state.localeConfig = { ...payload }
    },

    SET_FETCH_REQUEST: (state, payload) => {
      state.queueGeoFetch = payload
    },
    SET_LOCALE_SUGGESTION_STATUS: (state, status) => {
      state.showLocaleSuggestionModal = status
    },
    SET_LOCALE_SUGGESTED_VALUES: (
      state,
      payload /*{ countryCode, market, language }*/
    ) => {
      state.suggestedLocaleConfig = { ...payload }
    }
  },

  actions: {
    INIT_LOCALE({ commit, state }) {
      Bleach.debug.log("INIT_LOCALE")
      // Presume that the 'desired' localeConfig is indicated by the URL (eg, no path values present would mean UK store in en-GB, /us would be US store in en-US, /eu/nl would be Euro store in Dutch)
      const inferredStoreConfig = parseStoreConfigFromUrl(
        window.location.pathname,
        state.localeOptions
      )

      Bleach.debug.log(
        "inferredStoreConfig",
        JSON.stringify(inferredStoreConfig)
      )

      if (
        !state.localeConfig ||
        !state.localeConfig.countryCode ||
        !state.localeConfig.language ||
        !state.localeConfig.market
      ) {
        // locale is not already held in localStorage, so request geolocation
        Bleach.debug.log("NO LOCALE CONFIG")
        commit("SET_LOCALE_VALUES", inferredStoreConfig)
        commit("SET_FETCH_REQUEST", true)
      } else if (
        state.localeConfig?.countryCode !== inferredStoreConfig?.countryCode
      ) {
        Bleach.debug.log(
          "NOT THE SAME COUNTRY",
          state.localeConfig?.countryCode,
          inferredStoreConfig?.countryCode
        )
        // The existing localeConfig doesn't point to the same country as the inferredStoreConfig, so make the existing locale the suggestion (ie, return to your 'usual' store)
        commit("SET_LOCALE_SUGGESTED_VALUES", state.localeConfig)
        commit("SET_LOCALE_VALUES", inferredStoreConfig)
      } else {
        commit("SET_LOCALE_VALUES", inferredStoreConfig)
      }
    },

    GET_GEODATA({ commit, state, getters }) {
      if (state.queueGeoFetch) {
        return BleachAPI.getGeoLocation()
          .then(res => {
            let { countryCode } = res.data.geoLocation

            const availableRegions = getters.GET_REGION_OPTIONS

            if (
              availableRegions &&
              availableRegions.length > 0 &&
              !availableRegions
                .map(region => region.countryCode)
                .includes(countryCode)
            ) {
              countryCode = "GB"
            }

            // console.log("geoLoc", countryCode)

            if (state.localeConfig.countryCode != countryCode) {
              const suggestedConfig = getStoreConfig(
                countryCode,
                null,
                state.localeOptions
              )

              commit("SET_LOCALE_SUGGESTION_STATUS", true)
              commit("SET_LOCALE_SUGGESTED_VALUES", {
                ...suggestedConfig
              })
            }

            commit("SET_FETCH_REQUEST", false)
            commit("ui/SET_MODAL_QUEUE_ENABLED", null, { root: true })
          })
          .catch(err => console.log(err))
      } else if (state.suggestedLocaleConfig) {
        commit("SET_LOCALE_SUGGESTION_STATUS", true)
        commit("SET_FETCH_REQUEST", false)
        commit("ui/SET_MODAL_QUEUE_ENABLED", null, { root: true })
      } else {
        commit("ui/SET_MODAL_QUEUE_ENABLED", null, { root: true })
      }
    },

    CHANGE_LOCALE({ commit, state }, payload) {
      const { countryCode, language } = payload

      Bleach.debug.log("CHANGE_LOCALE", payload, countryCode, language)

      const intendedStore = getStoreConfig(
        countryCode,
        language,
        state.localeOptions
      )

      Bleach.debug.log("INTENDED STORE", intendedStore)

      const { countryPath, languagePath } = intendedStore

      Promise.all([
        commit("SET_LOCALE_VALUES", intendedStore),
        commit("cart/RESET_CART", null, { root: true }),
        commit("cart/SET_CART_ID", null, { root: true })
      ]).then(() => {
        Bleach.debug.log("currentRoute", getRouter(), getRouter().currentRoute)

        window.location.href = `${window.location.origin}${
          /* TODO: This should use the market getter and a local lookup */
          countryPath ? `/${countryPath}` : ""
        }${languagePath ? `/${languagePath}` : ""}${
          getRouter().currentRoute.fullPath
        }`
      })
    },
    REFUSE_CHANGE({ commit }) {
      commit("SET_LOCALE_SUGGESTION_STATUS", false)
      commit("SET_LOCALE_SUGGESTED_VALUES", null)
    }
  },

  getters: {
    GET_ACTIVE_STORE: state => {
      // base market on the selected countryCode
      return state.localeConfig.store
    },

    GET_ACTIVE_MARKET: state => {
      return state.localeConfig.market
    },

    GET_ACTIVE_LANGUAGE: state => {
      return (
        state.localeConfig.language || state.localeConfig.market.defaultLanguage
      )
    },

    GET_REGION_OPTIONS: state => {
      const shopifyRegions = state.localeOptions
        .map(store => {
          return store?.markets?.nodes.map(market => {
            return market?.conditions?.regionsCondition?.regions?.nodes.map(
              region => region.code
            )
          })
        })
        .flat(2)

      return regionOptions.filter(region =>
        shopifyRegions.includes(region.countryCode)
      )
    }
  }
}
